import { Link } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import type { AmountDisplayUnit } from '../../lib/finance-display.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { PartyLabel } from './PartyLabel.js';
import {
  partyBalanceStatus,
  sumRial,
  type PartyBalanceRow,
  type PartyBalanceStatus,
} from './OpenItemAggregates.js';

const STATUS_BADGE: Record<
  PartyBalanceStatus,
  { readonly tone: 'success' | 'warn' | 'danger'; readonly label: string }
> = {
  current: { tone: 'success', label: 'جاری' },
  overdue: { tone: 'warn', label: 'سررسید گذشته' },
  severe: { tone: 'danger', label: 'بیش از ۹۰ روز' },
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance, finAr «مانده مشتریان»
 *   (AP: «مانده تأمین‌کنندگان») — party, account, balance, overdue, max delay,
 *   status badge, row action, totals row «جمع · n».
 * status: PARTIAL_REAL — every figure is aggregated (BigInt) from the whole
 *   `GET .../finance/{ar|ap}/aging` item list, which the API returns unpaged
 *   (apps/api/src/finance/ar.controller.ts `aging` → queryAging →
 *   listOpenItems has no limit/offset), so the totals are true totals.
 *   Omitted: the reference's «یادآوری/پیگیری» row action — no reminder or
 *   dunning endpoint exists in apps/api/src/finance. Account column shows the
 *   GL control-account code only when the viewer may read the chart of
 *   accounts and all of the party's items share one control account.
 */
export function OpenItemsPartyBalances({
  kind,
  slug,
  base,
  rows,
  unit,
  accountCodeById,
  onShowItems,
  actions,
}: {
  readonly kind: 'ar' | 'ap';
  readonly slug: string;
  readonly base: string;
  readonly rows: readonly PartyBalanceRow[];
  readonly unit: AmountDisplayUnit;
  readonly accountCodeById: ReadonlyMap<string, string>;
  readonly onShowItems: (partyId: string) => void;
  readonly actions?: React.ReactNode;
}) {
  const isAr = kind === 'ar';
  const partyNoun = isAr ? 'مشتری' : 'تأمین‌کننده';
  const totalBalance = sumRial(rows.map((r) => r.balanceRial));
  const totalOverdue = sumRial(rows.map((r) => r.overdueRial));

  const columns: readonly V2GridColumn<PartyBalanceRow>[] = [
    {
      key: 'party',
      header: partyNoun,
      render: (row) => (
        <PartyLabel
          slug={slug}
          partyId={row.partyId}
          href={`${base}/master-data/parties/${row.partyId}`}
        />
      ),
    },
    {
      key: 'account',
      header: 'حساب',
      render: (row) => (
        <span className="ltr sk-num">
          {(row.controlAccountId && accountCodeById.get(row.controlAccountId)) || '—'}
        </span>
      ),
    },
    {
      key: 'balance',
      header: amountColumnHeader('مانده', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.balanceRial, unit)}</span>,
      footer: <span className="sk-num">{formatRialFa(totalBalance.toString(), unit)}</span>,
    },
    {
      key: 'overdue',
      header: amountColumnHeader('سررسید گذشته', unit),
      align: 'end',
      render: (row) =>
        BigInt(row.overdueRial) > 0n ? (
          <span
            className="sk-num"
            style={{
              fontWeight: 600,
              color:
                partyBalanceStatus(row) === 'severe'
                  ? 'var(--sk-danger)'
                  : 'color-mix(in oklch, var(--sk-warn) 60%, var(--sk-fg))',
            }}
          >
            {formatRialFa(row.overdueRial, unit)}
          </span>
        ) : (
          <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
        ),
      footer: <span className="sk-num">{formatRialFa(totalOverdue.toString(), unit)}</span>,
    },
    {
      key: 'delay',
      header: 'بیشترین تأخیر',
      render: (row) =>
        row.maxDaysPastDue > 0 ? (
          <span className="sk-num">{row.maxDaysPastDue.toLocaleString('fa-IR')} روز</span>
        ) : (
          <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
        ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => {
        const badge = STATUS_BADGE[partyBalanceStatus(row)];
        return <V2Badge tone={badge.tone}>{badge.label}</V2Badge>;
      },
    },
    {
      key: 'action',
      header: '',
      hideable: false,
      render: (row) => (
        <V2Button variant="ghost" size="sm" onClick={() => onShowItems(row.partyId)}>
          اقلام ({row.itemCount.toLocaleString('fa-IR')})
        </V2Button>
      ),
    },
  ];

  return (
    <V2DataGrid
      icon="user"
      title={isAr ? 'مانده مشتریان' : 'مانده تأمین‌کنندگان'}
      subtitle={`${isAr ? 'حساب‌های دریافتنی' : 'حساب‌های پرداختنی'} · فقط اقلام باز و تسویه ناقص · ${rows.length.toLocaleString('fa-IR')} ${partyNoun}`}
      actions={
        actions ?? (
          <Link to={`${base}/finance/treasury`}>
            <V2Button variant="primary" size="sm" icon="plus">
              {isAr ? 'ثبت دریافت' : 'ثبت پرداخت'}
            </V2Button>
          </Link>
        )
      }
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      densitySwitch={false}
      defaultDensity="comfortable"
      footerLabel={
        <>
          جمع · <span className="sk-num">{rows.length.toLocaleString('fa-IR')}</span> {partyNoun}
        </>
      }
      emptyState={
        <V2EmptyState
          icon="info"
          title="قلم بازی وجود ندارد"
          description={isAr ? 'همهٔ مطالبات تسویه شده است.' : 'همهٔ بدهی‌ها تسویه شده است.'}
        />
      }
    />
  );
}
